"use client";

/**
 * DashboardDownloadMenu — single download icon in the alert header (Dashboard
 * tab). Dropdown offers PNG · PDF · HTML.
 *
 * Fetches the SAME snapshot SWR key as AlertDashboardView so there's no extra
 * network round-trip (deduped). PNG/PDF need the PKA-generated `image_url`;
 * HTML uses the raw dashboard html. Options that have no source are hidden.
 */

import { useState } from "react";
import useSWR from "swr";
import { Download, FileImage, FileText, FileCode2, Loader2 } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiClient, type AlertSnapshot } from "@/lib/api-client";
import { useAlertPublicView } from "./AlertPaywallContext";

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function DashboardDownloadMenu({
  alertId,
  isOwner = false,
}: {
  alertId: string;
  isOwner?: boolean;
}) {
  const publicView = useAlertPublicView();
  const [busy, setBusy] = useState(false);

  const mode: "latest" | "first" =
    isOwner || (publicView.view === "live" && publicView.canGoLive)
      ? "latest"
      : "first";

  // Same key shape as AlertDashboardView → shared SWR cache, no extra fetch.
  const swrKey = alertId ? `alert-dash-${alertId}-${mode}` : null;
  const { data } = useSWR<{ snapshot: AlertSnapshot } | null>(
    swrKey,
    async () => {
      if (!alertId) return null;
      try {
        return mode === "latest"
          ? await apiClient.getAlertSnapshotLatest(alertId)
          : await apiClient.getAlertSnapshotFirst(alertId);
      } catch {
        return null;
      }
    },
    { revalidateOnFocus: false, dedupingInterval: 5_000, shouldRetryOnError: false },
  );

  const snapshot = data?.snapshot ?? null;
  const html = snapshot?.html ?? null;
  const imageUrl = snapshot?.image_url ?? null;
  // We can ALWAYS produce PNG/PDF: either from the backend image_url (fast
  // path) or by rendering the dashboard html to a canvas on the fly. So all
  // three options show whenever we have either source.
  const hasAny = Boolean(html || imageUrl);

  const downloadHtml = () => {
    if (!html) return;
    triggerBlobDownload(new Blob([html], { type: "text/html" }), `${alertId}-dashboard.html`);
  };

  // Returns a PNG dataURL + its pixel size. Prefers the backend image; else
  // renders the html in an off-screen iframe and snapshots it with html2canvas.
  const buildPngDataUrl = async (): Promise<{ dataUrl: string; width: number; height: number }> => {
    if (imageUrl) {
      const dataUrl = await fetchAsDataUrl(imageUrl);
      const { width, height } = await readImageSize(dataUrl);
      return { dataUrl, width, height };
    }
    if (!html) throw new Error("no source");
    return renderHtmlToPng(html);
  };

  const downloadPng = async () => {
    setBusy(true);
    try {
      const { dataUrl } = await buildPngDataUrl();
      const blob = await (await fetch(dataUrl)).blob();
      triggerBlobDownload(blob, `${alertId}-dashboard.png`);
    } catch {
      if (imageUrl) window.open(imageUrl, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const { dataUrl, width, height } = await buildPngDataUrl();
      const orientation = width >= height ? "landscape" : "portrait";
      const pdf = new jsPDF({ orientation, unit: "px", format: [width, height] });
      pdf.addImage(dataUrl, "PNG", 0, 0, width, height);
      pdf.save(`${alertId}-dashboard.pdf`);
    } catch {
      if (imageUrl) window.open(imageUrl, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  };

  if (!hasAny) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Download dashboard"
          aria-label="Download dashboard"
          className={[
            "inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0",
            "border border-border/60 bg-transparent text-foreground/80",
            "hover:text-foreground hover:bg-muted transition-colors",
            FOCUS_RING,
          ].join(" ")}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-[180px]">
        <DropdownMenuItem onClick={downloadPng} disabled={busy}>
          <FileImage className="w-3.5 h-3.5 mr-2" /> Download PNG
        </DropdownMenuItem>
        <DropdownMenuItem onClick={downloadPdf} disabled={busy}>
          <FileText className="w-3.5 h-3.5 mr-2" /> Download PDF
        </DropdownMenuItem>
        {html && (
          <DropdownMenuItem onClick={downloadHtml} disabled={busy}>
            <FileCode2 className="w-3.5 h-3.5 mr-2" /> Download HTML
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

async function fetchAsDataUrl(url: string): Promise<string> {
  const res = await fetch(url, { mode: "cors" });
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Render a self-contained dashboard HTML string to a PNG dataURL by writing it
 * into a hidden, same-origin iframe and snapshotting the body with html2canvas.
 * Off-screen (not display:none — html2canvas needs real layout), fixed width so
 * charts/tables lay out predictably.
 */
async function renderHtmlToPng(
  html: string,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const WIDTH = 1200;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = `${WIDTH}px`;
  iframe.style.height = "800px";
  iframe.style.border = "0";
  iframe.style.background = "#ffffff";
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error("no iframe document");
    doc.open();
    doc.write(html);
    doc.close();

    // Wait for load + a beat for fonts/charts to paint.
    await new Promise((resolve) => {
      if (doc.readyState === "complete") resolve(null);
      else iframe.onload = () => resolve(null);
    });
    await new Promise((r) => setTimeout(r, 600));

    const body = doc.body;
    const fullHeight = Math.max(
      body.scrollHeight,
      doc.documentElement.scrollHeight,
      800,
    );
    iframe.style.height = `${fullHeight}px`;

    const canvas = await html2canvas(body, {
      backgroundColor: "#ffffff",
      width: WIDTH,
      height: fullHeight,
      windowWidth: WIDTH,
      windowHeight: fullHeight,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return {
      dataUrl: canvas.toDataURL("image/png"),
      width: canvas.width,
      height: canvas.height,
    };
  } finally {
    iframe.remove();
  }
}

function readImageSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
